import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  TrackByFunction,
  ViewChild
} from '@angular/core';
import { CollectionForm } from '../../../../../../models/ts/collection-form.model';
import { TrainingAssessmentDto } from '../../../../../../models/ts/training-assessment-dto.model';
import { TranslatePipe } from '../../../../../shared/pipes/translate/translate.pipe';
import { CheckBoxModule, RadioButtonModule, TextAreaModule } from '@progress/kendo-angular-inputs';
import { EditorModule } from '@progress/kendo-angular-editor';
import { IconComponent } from '../../../../../shared/components/ui/icon/icon.component';
import { GridModule } from '@progress/kendo-angular-grid';
import { GridColumnType } from '../../../../../../models/ts/grid-column-type.model';
import { GaugesModule } from '@progress/kendo-angular-gauges';
import { TrnUserSkillGradeDto } from '../../../../../../models/ts/trn-user-skill-grade-dto.model';
import { TrnSkillGradeDto } from '../../../../../../models/ts/trn-skill-grade-dto.model';
import { TrnSkillGradeExpiracyStatus } from '../../../../../../models/ts/trn-skill-grade-expiracy-status.model';
import { FormTrainingappAssessmentSliderComponent } from './form-trainingapp-assessment-slider/form-trainingapp-assessment-slider.component';
import { TrnUserDto } from '../../../../../../models/ts/trn-user-dto.model';
import { asyncScheduler, BehaviorSubject, debounceTime, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridComponent } from '@progress/kendo-angular-grid/grid.component';
import { resizeLastGridColumn } from 'src/app/shared/components/grid/helpers/resize-last-grid-column';
import { SetAllGradesAssessmentComponent } from './set-all-grades-assessment/set-all-grades-assessment.component';
import { Dialog } from '@angular/cdk/dialog';
import { ProtectedFieldValueType } from 'src/models/ts/protected-field-value-type.model';
import { DatePipe } from 'src/app/shared/pipes/dates/date.pipe';
import { QualificationSkillMatrixApiService } from 'src/app/api/bizzmine/qualification-skill-matrix/qualification-skill-matrix-api.service';
import { FileUploadComponent } from 'src/app/shared/components/file-upload/file-upload/file-upload.component';
import { SimpleDocumentTemplateDto } from 'src/models/ts/simple-document-template-dto.model';
import { MediaCommitDto } from 'src/models/ts/media-commit-dto.model';
import { DocumentTemplateDto } from 'src/models/ts/document-template-dto.model';
import { FileUploadSelectTemplateDialogComponent } from 'src/app/shared/components/file-upload/file-upload/file-upload-select-template-dialog/file-upload-select-template-dialog.component';
import { CertificatesFoundWarningComponent } from 'src/app/features/bizzmine/form/components/form-trainingapp-assessment/certificates-found-warning/certificates-found-warning.component';
import { Store } from '@ngrx/store';
import { formsActions } from 'src/app/store/features/forms/forms-actions';

@Component({
  selector: 'bizz-form-trainingapp-assessment',
  standalone: true,
  imports: [
    TranslatePipe,
    CheckBoxModule,
    EditorModule,
    IconComponent,
    RadioButtonModule,
    TextAreaModule,
    GridModule,
    GaugesModule,
    FormTrainingappAssessmentSliderComponent,
    DatePipe,
    FileUploadComponent
  ],
  templateUrl: './form-trainingapp-assessment.component.html',
  styleUrl: './form-trainingapp-assessment.component.scss'
})
export class FormTrainingappAssessmentComponent implements AfterViewInit {

  public data: TrainingAssessmentDto;

  @Input({ required: true }) public set formData(data: CollectionForm) {
    const found = structuredClone(data.TrainingAssessment);
    found.Participants.forEach(participant => {
      participant.mediaCommitDto = undefined;
      if (participant.Certificate != null && participant.Certificate.MediaID > 0) {
        const mapped: MediaCommitDto = {
          FileName: participant.Certificate.Filename,
          MediasID: participant.Certificate.MediaID,
          fromTemplate: participant.Certificate.CopyFile,
          Type: 'Certificate'
        } as MediaCommitDto;
        participant.mediaCommitDto = mapped;
      }
    });
    this.data = found;
  }

  @Input({ required: true }) public formId: string;

  @Input() public readonly: boolean;
  public ExpiracyStatus = TrnSkillGradeExpiracyStatus;
  public readonly ProtectedFieldValueType = ProtectedFieldValueType;
  public colors = [
    {
      from: 0,
      color: '#3fad10'
    }
  ];
  protected readonly gridColumnType = GridColumnType;

  private emitUpdate$ = new BehaviorSubject<void>(undefined);
  @ViewChild('trainingAssessmentKendoGrid', { read: ElementRef })
  private kendoGridElement: ElementRef;
  @ViewChild('trainingAssessmentKendoGrid')
  private kendoGridComponent: GridComponent;
  private totalGridWidth: number;
  private combinedWidthOfOtherColumns: number;
  private initialLastColumnWidth: number;
  private qualificationSkillMatrixApiService = inject(QualificationSkillMatrixApiService);
  private readonly store$ = inject(Store);

  public constructor(private destroyRef: DestroyRef,
                     private documentEventsService: DocumentEventsService,
                     private dialog: Dialog) {
    this.emitUpdate$.pipe(takeUntilDestroyed(this.destroyRef),
      debounceTime(500)).subscribe(() => {
      this.store$.dispatch(formsActions.updateTrainingAssessment({
        formId: this.formId, trainingAssessment: this.data
      }));
    });
    this.documentEventsService.windowResizeEvent.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300)
    ).subscribe(() => {
      this.resizeLastGridColumn();
    });
  }

  public fileChanged(dataItem: TrnUserDto, event: MediaCommitDto | undefined): void {
    dataItem.Certificate = undefined;
    dataItem.mediaCommitDto = undefined;
    if (event?.MediasID) {
      dataItem.Certificate = {
        MediaID: event.MediasID,
        Filename: event.FileName,
        Extension: event.FileName.split('.').pop() ?? '',
        CopyFile: event.fromTemplate ?? false
      };
    }
    this.emitUpdate();
  }

  public templateInfo: SimpleDocumentTemplateDto | undefined = undefined;
  public documentTemplate: DocumentTemplateDto | undefined;

  public initTemplateInfo(): void {
    if (this.data.TrainingType != ProtectedFieldValueType.SkillQualificationNone_Qualification) {
      return;
    }
    //showAbsentees is defaulted to false for qualification training apps
    this.showAbsentees = false;
    this.qualificationSkillMatrixApiService.getCertificateTemplateInfo(this.data.TrainingAppID).pipe(
      take(1)
    ).subscribe(found => {
      this.documentTemplate = found;
      this.templateInfo = {
        TemplateViewsID: this.documentTemplate.TemplateViewsID,
        TemplateViewListID: this.documentTemplate.TemplateViewListID,
        StartFromTemplate: this.documentTemplate.StartFromTemplate
      } as SimpleDocumentTemplateDto;
    });
  }

  public ngAfterViewInit(): void {
    this.initTemplateInfo();
    asyncScheduler.schedule(() => {
      this.kendoGridComponent.columnResize.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.resizeLastGridColumn();
      });
      this.resizeLastGridColumn();
    });
  }

  public emitUpdate(): void {
    this.emitUpdate$.next();
  }

  public presentChanged(user: TrnUserDto, value: boolean): void {
    if (this.data.TrainingType == ProtectedFieldValueType.SkillQualificationNone_Qualification) {
      user.QualificationAchieved = value;
    }
    user.Present = value;
    if (!user.Present) {
      user.Certificate = undefined;
    }
    this.emitUpdate();
  }

  public achievedChanged(user: TrnUserDto, value: boolean): void {
    user.QualificationAchieved = value;
    if (!user.QualificationAchieved) {
      user.Certificate = undefined;
    }
    this.emitUpdate();
  }

  public onSkillChanged(skill: TrnUserSkillGradeDto, participants: TrnUserDto): void {
    participants.NewUserSkillGrade = skill;
    this.emitUpdate();
  }

  public getScore(skillGrade: TrnUserSkillGradeDto, defaultValue: boolean): TrnSkillGradeDto | null {
    if (!this.data || !this.data.SkillGrades || !skillGrade || this.data.SkillGrades.length == 0) {
      return null;
    }
    const found = this.data.SkillGrades.find(x => x.ID == skillGrade.SkillGradeID);
    if (found) {
      return found;
    }
    if (defaultValue) {
      return this.data.SkillGrades[0];
    }
    return null;
  }

  private resizeLastGridColumn(): void {
    if (this.kendoGridElement == undefined || this.kendoGridComponent == undefined) {
      throw new Error('kendoGridElement is undefined');
    }
    resizeLastGridColumn(this.kendoGridComponent.columns.toArray(),
      this.kendoGridElement.nativeElement.offsetWidth,
      this.kendoGridComponent.group?.length ?? 0,
      this.initialLastColumnWidth);
  }

  public showAbsentees = true;

  public toggleAbsentees(): void {
    this.showAbsentees = !this.showAbsentees;
  }

  public createCertificate(): void {
    if (this.data.Participants.some(_ => _.Certificate != undefined && _.Certificate.MediaID != undefined)) {
      this.dialog.open<boolean | undefined>(CertificatesFoundWarningComponent, {
        data: {
          count: this.data.Participants.filter(
            participant => participant.Certificate != undefined && participant.Certificate.MediaID != undefined
          ).length
        }
      }).closed.pipe(take(1))
        .subscribe((result) => {
          if (result == true) {
            this._createCertificate();
          }
        });
      return;
    }
    this._createCertificate();
  }


  public _createCertificate(): void {
    this.data.Participants.forEach(participant => {
      participant.mediaCommitDto = undefined;
    });
    if (this.documentTemplate == undefined) {
      return;
    }
    if (this.documentTemplate.TemplateViewListID == undefined || this.documentTemplate.TemplateViewListID == 0) {
      return;
    }
    if (this.documentTemplate.DefaultTemplate == undefined || this.documentTemplate.DefaultTemplate.Templates == undefined || this.documentTemplate.DefaultTemplate.Templates.length == 0) {
      return;
    }
    if (this.documentTemplate.DefaultTemplate.Templates.length == 1) {
      // only -> TODO -> instantntly select item
      const mapped: MediaCommitDto = {
        FileName: this.documentTemplate.DefaultTemplate.Templates[0].Text,
        MediasID: this.documentTemplate.DefaultTemplate.Templates[0].FileId,
        fromTemplate: true
      } as MediaCommitDto;
      this.data.Participants.forEach(participant => {
        if (participant.Present && participant.QualificationAchieved) {
          participant.mediaCommitDto = mapped;
        }
      });
      return;
    }
    let viewListId = this.documentTemplate?.TemplateViewListID;
    this.dialog.open<any>(FileUploadSelectTemplateDialogComponent, {
      data: {
        viewListId: viewListId, override: {
          canViewDraftDocuments: undefined
        }
      },
      disableClose: true
    })
      .closed.pipe(
      take(1)
    ).subscribe(selectedTemplate => {
      // when we set we may NOT forget to emit a file changed event also -> needed for outside form use (checkin modal etc);
      if (selectedTemplate != null) {
        const mapped: MediaCommitDto = {
          FileName: selectedTemplate.Title,
          MediasID: selectedTemplate.File,
          fromTemplate: true
        } as MediaCommitDto;
        this.data.Participants.forEach(participant => {
          if (participant.Present && participant.QualificationAchieved) {
            participant.mediaCommitDto = mapped;
          }
        });
        return;
      }
    });
  }

  public applyFilter(items: TrnUserDto[]): TrnUserDto[] {
    if (!this.showAbsentees) {
      return items.filter(_ => _.Present);
    }
    return items;
  }

  public trackBy: TrackByFunction<any> = (index: number, row: any) => {
    return row.data.ID;
  };

  /**
   * Function for opening set score modal for all grades
   */
  public openSetAllGradesModal(): void {
    const dialogRef = this.dialog.open<{
      skillGradeId: number,
      skillGradeChangeReason: string
    }>(SetAllGradesAssessmentComponent,
      { data: this.data });
    dialogRef.closed.subscribe({
      next: (data) => {
        if (data) {
          this.data.Participants.forEach((participant) => {
            if (participant.NewUserSkillGrade) {
              participant.NewUserSkillGrade.SkillGradeID = data.skillGradeId;
              participant.Remarks = data.skillGradeChangeReason;
            }
          });
          this.emitUpdate();
        }
      },
      complete: () => {

      }
    });
  }
}
